'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from '@mui/material';
import { FormTextField } from '@/components/common/FormComponents';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useAuthStore } from '@/stores/authStore';
import { PrivilegeEnum } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { UserSettingsDto } from '@/api/ApiClient';
import { ProfilePreferencesTab } from '@/components/preferences/ProfilePreferencesTab';
import { RecipeFavoritesTab } from '@/components/preferences/RecipeFavoritesTab';
import { FileMetadataTab } from '@/components/preferences/FileMetadataTab';

interface UserSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

type TabId = 'settings' | 'profile' | 'favorites' | 'fileMetadata';

const UserSettingsDialog: React.FC<UserSettingsDialogProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('settings');
  const [saving, setSaving] = useState(false);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { currentUser, isAuthorized, fetchUserDataFromServer } = useAuthStore();

  // Form state - initialize from currentUser settings
  const [showLastProject, setShowLastProject] = useState(() => currentUser?.settings?.showLastProject ?? false);
  const [showLastSample, setShowLastSample] = useState(() => currentUser?.settings?.showLastSample ?? false);
  const [sortNewestSampleFirst, setSortNewestSampleFirst] = useState(
    () => currentUser?.settings?.sortNewestSampleFirst ?? false
  );
  const [showShippedSamples, setShowShippedSamples] = useState(
    () => currentUser?.settings?.showShippedSamples ?? false
  );
  const [nbOfRecentDays, setNbOfRecentDays] = useState<number | undefined>(() => currentUser?.settings?.nbOfRecentDays);

  // Check if user has privileges for tabs
  const showFavoritesTab = isAuthorized(PrivilegeEnum.RecipeFavorite, 'read');
  const showCharInstrumentTab = isAuthorized(PrivilegeEnum.ConfigureFileMetaData, 'read');

  // Build array of available tabs
  const availableTabs = useMemo(() => {
    const tabs: TabId[] = ['settings', 'profile'];
    if (showFavoritesTab) tabs.push('favorites');
    if (showCharInstrumentTab) tabs.push('fileMetadata');
    return tabs;
  }, [showFavoritesTab, showCharInstrumentTab]);

  // Get tab index for MUI Tabs component
  const tabIndex = useMemo(() => {
    return availableTabs.indexOf(activeTab);
  }, [availableTabs, activeTab]);

  // Reset form to current user settings when dialog closes
  const handleClose = useCallback(() => {
    const settings = currentUser?.settings;
    setShowLastProject(settings?.showLastProject ?? false);
    setShowLastSample(settings?.showLastSample ?? false);
    setSortNewestSampleFirst(settings?.sortNewestSampleFirst ?? false);
    setShowShippedSamples(settings?.showShippedSamples ?? false);
    setNbOfRecentDays(settings?.nbOfRecentDays);
    onClose();
  }, [currentUser, onClose]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!currentUser?.settings) return;

    setSaving(true);
    try {
      const settings: UserSettingsDto = {
        ...currentUser.settings,
        showLastProject,
        showLastSample,
        sortNewestSampleFirst,
        showShippedSamples,
        nbOfRecentDays,
      };

      await backend.user.saveSettings(settings);
      showSnackbar('Settings saved successfully', 'success');

      // Refresh user data to get updated settings
      await fetchUserDataFromServer();

      onClose();
    } catch (err: any) {
      log.error('[UserSettingsDialog] Error saving settings:', err);
      showSnackbar(getProblemDetail(err) ?? 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  }, [
    currentUser,
    showLastProject,
    showLastSample,
    sortNewestSampleFirst,
    showShippedSamples,
    nbOfRecentDays,
    backend,
    showSnackbar,
    fetchUserDataFromServer,
    onClose,
  ]);

  const handleTabChange = (_event: React.ChangeEvent<object>, newValue: number) => {
    const tabId = availableTabs[newValue];
    if (tabId) {
      setActiveTab(tabId);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>User Settings</DialogTitle>
      <Tabs value={tabIndex} onChange={handleTabChange}>
        <Tab label="Settings" />
        <Tab label="Profile" />
        {showFavoritesTab ? <Tab label="Recipe Favorites" /> : null}
        {showCharInstrumentTab ? <Tab label="File Metadata" /> : null}
      </Tabs>
      <DialogContent>
        {activeTab === 'settings' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sortNewestSampleFirst}
                  onChange={(e) => setSortNewestSampleFirst(e.target.checked)}
                />
              }
              label="Most recent sample on top"
              title="Sort samples in the tree from newest to oldest"
            />
            <FormControlLabel
              control={
                <Checkbox checked={showShippedSamples} onChange={(e) => setShowShippedSamples(e.target.checked)} />
              }
              label="Show shipped samples in Sample Tree"
              title="If unchecked, shipped samples are only shown as part of their shipping"
            />
            <FormTextField
              label="Max age of samples in 'Recent Samples'"
              type="number"
              fullWidth
              value={nbOfRecentDays?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value;
                setNbOfRecentDays(value === '' ? undefined : parseInt(value, 10));
              }}
            />
          </Box>
        ) : null}
        {activeTab === 'profile' ? <ProfilePreferencesTab /> : null}
        {activeTab === 'favorites' ? (
          <Box sx={{ mt: 2 }}>
            <RecipeFavoritesTab />
          </Box>
        ) : null}
        {activeTab === 'fileMetadata' ? (
          <Box sx={{ mt: 2 }}>
            <FileMetadataTab />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={saving}>
          Close
        </Button>
        {activeTab === 'settings' ? (
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export { UserSettingsDialog };
